import * as React from "react"

import { cn } from "~/utils/helpers"

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
  ),
)
Table.displayName = "Table"

const SimpleTable = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
  ),
)
SimpleTable.displayName = "SimpleTable"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("border-b border-base bg-subtle font-normal", className)}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const SimpleTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("bg-medium label-sm text-fg-subtle overflow-hidden", className)}
    {...props}
  />
))
SimpleTableHeader.displayName = "SimpleTableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("bg-base", className)} {...props} />
))
TableBody.displayName = "TableBody"

const SimpleTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={cn("", className)} {...props} />)
SimpleTableBody.displayName = "SimpleTableBody"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "hover:bg-base-hover rounded-[8px] data-[state=selected]:bg-base-hover border-b border-base transition-colors",
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = "TableRow"

const SimpleTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "hover:bg-base-hover data-[state=selected]:bg-muted transition-colors ",
      className,
    )}
    {...props}
  />
))
SimpleTableRow.displayName = "SimpleTableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 py-3 text-left label-sm text-[10px] text-fg-base align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] first:pl-5 last:pr-5 first:border-l-0 border-l-1 border-base",
      className,
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const SimpleTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "label-base text-left align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] px-2 py-[5px] first:rounded-l-[8px] last:rounded-r-[8px]",
      className,
    )}
    {...props}
  />
))
SimpleTableHead.displayName = "SimpleTableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2.5 align-middle text-fg-base [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] first:pl-5 last:pr-5 first:border-l-0 border-l-1 border-base",
      className,
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const SimpleTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-2 py-1 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] first:rounded-l-[8px] last:rounded-r-[8px]",
      className,
    )}
    {...props}
  />
))
SimpleTableCell.displayName = "SimpleTableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn("text-fg-muted mt-4 text-sm", className)} {...props} />
))
TableCaption.displayName = "TableCaption"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("bg-base-hover border-t font-normal last:[&>tr]:border-b-0", className)}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  SimpleTable,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableBody,
  SimpleTableRow,
}
